// src/web/client/src/pages/PrivacyPolicy.jsx
// Public privacy policy page — required for the Shopify App Store listing.
// MUST stay public: do not wrap in PrivateRoute and do not make any API calls
// from this component. The route is registered in pages/index.jsx BEFORE the
// catch-all /* PrivateRoute branch.

import React from 'react';

const LAST_UPDATED = 'July 14, 2026';
const CONTACT_EMAIL = 'founder@ecomoria.com';
const ADDRESS = '14 Abuhav St., Jerusalem, Israel';

const SUB_PROCESSORS = [
  { name: 'MongoDB Atlas', purpose: 'Database (cloud)', data: 'Store, order, and customer data (names, emails); message records' },
  { name: 'Render', purpose: 'Application hosting', data: 'All App data passes through hosted frontend and backend' },
  { name: 'Shopify', purpose: 'Source platform', data: 'Order and customer data (via Shopify Admin API and webhooks)' },
  {
    name: 'OpenAI',
    purpose: 'AI email content generation',
    data:
      "Customer name, products, order number, order total. Inputs submitted to the API are not used to train OpenAI's models by default; OpenAI may retain inputs for a limited period (approximately 30 days) for abuse monitoring, then deletes them."
  },
  { name: 'Resend', purpose: 'Email delivery', data: 'Customer email address and email content (sent from noreply@ecomoria.com)' }
];

function Section({ id, title, children }) {
  return (
    <section id={id} className="mt-10 first:mt-0">
      <h2 className="text-2xl font-semibold text-gray-900 mb-3">{title}</h2>
      <div className="space-y-3 text-gray-700 leading-relaxed">{children}</div>
    </section>
  );
}

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-white text-gray-800 antialiased">
      {/* Top bar — keeps brand identity without forcing nav into a marketing site */}
      <header className="border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2 text-gray-900 font-semibold">
            <span
              aria-hidden="true"
              className="inline-flex w-8 h-8 rounded-md items-center justify-center text-white"
              style={{ background: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)' }}
            >
              E
            </span>
            <span>Ecomoria</span>
          </a>
          <a
            href={`mailto:${CONTACT_EMAIL}`}
            className="text-sm text-indigo-600 hover:text-indigo-800"
          >
            Contact
          </a>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12">
        <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight">
          Privacy Policy
        </h1>
        <p className="mt-2 text-sm text-gray-500">Last updated: {LAST_UPDATED}</p>

        <div
          role="note"
          className="mt-6 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
        >
          <strong>Template notice:</strong> This document was prepared as a starting template
          based on Ecomoria's actual data practices. It is <strong>not legal advice</strong>.
          Have it reviewed by a qualified attorney before publishing or relying on it.
        </div>

        <Section id="introduction" title="1. Introduction">
          <p>
            Ecomoria ("we," "us," "our") provides an AI-powered post-purchase email automation
            service for Shopify merchants. This Privacy Policy explains how we collect, use,
            store, and protect personal data in connection with our application (the "App").
          </p>
          <ul className="list-none pl-0 space-y-1">
            <li><strong>Company:</strong> Ecomoria</li>
            <li>
              <strong>Contact:</strong>{' '}
              <a className="text-indigo-600 hover:text-indigo-800" href={`mailto:${CONTACT_EMAIL}`}>
                {CONTACT_EMAIL}
              </a>
            </li>
            <li><strong>Address:</strong> {ADDRESS}</li>
          </ul>
        </Section>

        <Section id="role" title="2. Our role: data processor">
          <p>
            When a Shopify merchant installs and uses our App,{' '}
            <strong>the merchant is the data controller</strong> and{' '}
            <strong>Ecomoria acts as a data processor</strong> on their behalf. We process
            personal data only to provide the App's services — generating and sending
            personalized post-purchase emails — and according to the merchant's instructions.
          </p>
          <p>
            For data about the merchant's own account (e.g., store details, billing), we act as
            a controller.
          </p>
        </Section>

        <Section id="data" title="3. What data we process">
          <p><strong>On behalf of the merchant (their customers' data):</strong></p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Customer name</li>
            <li>Customer email address</li>
            <li>Order data (products purchased, order number, order total, order dates)</li>
          </ul>
          <p className="mt-4"><strong>For the merchant's account:</strong></p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Store domain and store details</li>
            <li>Billing and subscription status</li>
          </ul>
          <p>
            We process this data solely to generate and send AI-personalized post-purchase emails
            (such as thank-you messages, review requests, and customer-segment-based
            communications) on the merchant's behalf.
          </p>
          <p>
            We do <strong>not</strong> sell personal data. We do <strong>not</strong> use it for
            advertising.
          </p>
        </Section>

        <Section id="use" title="4. How the data is used">
          <ul className="list-disc pl-6 space-y-2">
            <li>
              <strong>Email generation:</strong> Customer name, products, order number, and
              order total are used to generate personalized email content.
            </li>
            <li>
              <strong>Email delivery:</strong> Customer email addresses are used to send the
              generated emails.
            </li>
            <li>
              <strong>Engagement and analytics:</strong> Order data (order count, total spent,
              recency) is used to score customer engagement and segment customers (e.g.,
              identifying repeat or high-value customers), and to attribute revenue to messages.
            </li>
          </ul>
        </Section>

        <Section id="sub-processors" title="5. Sub-processors">
          <p>
            We use the following third-party service providers ("sub-processors") to operate the
            App. Each processes data only as needed to provide its service:
          </p>
          <div className="overflow-x-auto mt-2 -mx-4 sm:mx-0">
            <table className="min-w-full text-sm border border-gray-200 rounded-md">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-4 py-2 text-left font-semibold text-gray-900 border-b border-gray-200">Sub-processor</th>
                  <th scope="col" className="px-4 py-2 text-left font-semibold text-gray-900 border-b border-gray-200">Purpose</th>
                  <th scope="col" className="px-4 py-2 text-left font-semibold text-gray-900 border-b border-gray-200">Data involved</th>
                </tr>
              </thead>
              <tbody>
                {SUB_PROCESSORS.map((sp, i) => (
                  <tr key={sp.name} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-4 py-3 align-top font-medium text-gray-900 border-b border-gray-200">{sp.name}</td>
                    <td className="px-4 py-3 align-top text-gray-700 border-b border-gray-200">{sp.purpose}</td>
                    <td className="px-4 py-3 align-top text-gray-700 border-b border-gray-200">{sp.data}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>

        <Section id="retention" title="6. Data retention and deletion">
          <p>
            We retain personal data only as long as needed to provide the service, or as the
            merchant instructs.
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              <strong>Customer data deletion request (<code>customers/redact</code>):</strong> When a
              merchant or customer requests deletion of a specific customer's data, we{' '}
              <strong>anonymize</strong> that customer's personal data (name, email, phone,
              billing address) in our records — replacing it with a redacted placeholder — so
              that no personal data remains, while preserving anonymized statistical records.
            </li>
            <li>
              <strong>Shop data deletion (<code>shop/redact</code>):</strong> When a merchant
              uninstalls the App, Shopify notifies us, and we{' '}
              <strong>delete all of that shop's data</strong> from our systems (orders, messages,
              logs, and the shop record).
            </li>
            <li>
              <strong>Data access request (<code>customers/data_request</code>):</strong> We
              compile and provide the personal data we hold about a specific customer upon
              request.
            </li>
          </ul>
          <p>
            These requests are handled through Shopify's mandatory compliance webhooks, with
            each request cryptographically verified (HMAC) to ensure authenticity.
          </p>
        </Section>

        <Section id="security" title="7. Data security">
          <p>We protect personal data using industry-standard measures, including:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Encrypted connections (HTTPS/TLS) for data in transit</li>
            <li>Access controls limiting who can access data</li>
            <li>Cryptographic verification (HMAC) of incoming compliance requests</li>
            <li>
              Reputable cloud infrastructure providers (MongoDB Atlas, Render) with their own
              security certifications
            </li>
          </ul>
          <p>
            No method of transmission or storage is 100% secure, but we take reasonable steps to
            protect personal data.
          </p>
        </Section>

        <Section id="rights" title="8. Your rights">
          <p>
            Depending on your jurisdiction (e.g., GDPR, CPRA), individuals may have rights to
            access, correct, delete, or restrict processing of their personal data. Because we
            act as a processor on behalf of merchants,{' '}
            <strong>data subjects should direct such requests to the merchant</strong> (the store
            they purchased from), who can then instruct us. You may also contact us at{' '}
            <a className="text-indigo-600 hover:text-indigo-800" href={`mailto:${CONTACT_EMAIL}`}>
              {CONTACT_EMAIL}
            </a>
            .
          </p>
        </Section>

        <Section id="transfers" title="9. International data transfers">
          <p>
            Data may be processed in countries other than your own (including the United States
            and Israel, where our providers and company operate). We rely on our sub-processors'
            safeguards for international transfers.
          </p>
        </Section>

        <Section id="children" title="10. Children's privacy">
          <p>
            The App is not directed at children, and we do not knowingly process data of children
            under 16.
          </p>
        </Section>

        <Section id="changes" title="11. Changes to this policy">
          <p>
            We may update this Privacy Policy from time to time. The "Last updated" date reflects
            the latest version.
          </p>
        </Section>

        <Section id="contact" title="12. Contact">
          <p>
            For questions about this Privacy Policy or our data practices:
            <br />
            <strong>Ecomoria</strong> —{' '}
            <a className="text-indigo-600 hover:text-indigo-800" href={`mailto:${CONTACT_EMAIL}`}>
              {CONTACT_EMAIL}
            </a>{' '}
            — {ADDRESS}
          </p>
        </Section>
      </main>

      <footer className="border-t border-gray-200 mt-16">
        <div className="max-w-3xl mx-auto px-6 py-6 text-center text-sm text-gray-500">
          &copy; {new Date().getFullYear()} Ecomoria. All rights reserved.
        </div>
      </footer>
    </div>
  );
}
